import { Box, HStack, Text } from '@chakra-ui/react'
import { Map } from 'lucide-react'
import { memo, useEffect, useMemo, useRef } from 'react'
import { CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet'
import type { FieldEngineerRecommendation } from '@/types/ticket'
import 'leaflet/dist/leaflet.css'

const Y_OFFSETS = [0, 40, -25, 24, -42, 12]
const DEFAULT_CENTER: [number, number] = [14.5995, 120.9842]

interface FEDispatchMapProps {
  recommendations: FieldEngineerRecommendation[]
  primaryUserId?: number | null
  backupUserId?: number | null
  /** Alias for backupUserId (additional FE pin highlight). */
  additionalUserId?: number | null
  referenceType?: 'store' | 'location' | null
  referenceLabel?: string | null
  storeCode?: string | null
  storeLatitude?: number | null
  storeLongitude?: number | null
  conflictUserId?: number | null
  onPinClick?: (rec: FieldEngineerRecommendation) => void
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

function pinColor(
  fe: FieldEngineerRecommendation,
  isPrimary: boolean,
  isBackup: boolean,
  isConflict: boolean,
): string {
  if (isConflict) return '#e24b4a'
  if (fe.is_locked && !isPrimary && !isBackup) return '#eda100'
  if (fe.available_at_schedule === false) return '#e24b4a'
  const label = fe.schedule_label?.toLowerCase() ?? ''
  if (label.includes('from') || label.includes('capacity')) return '#eda100'
  if (fe.available_at_schedule === true || label.includes('free')) return '#1baf7a'
  return '#eda100'
}

function feOpacity(fe: FieldEngineerRecommendation, isPrimary: boolean, isBackup: boolean): number {
  if (isPrimary || isBackup) return 1
  if (fe.has_required_certifications === false) return 0.3
  if (fe.available_at_schedule === false && !fe.is_locked) return 0.5
  return 1
}

function offsetLatLng(centerLat: number, centerLng: number, distanceKm: number, bearingDeg: number): [number, number] {
  const latOffset = (distanceKm / 111) * Math.cos((bearingDeg * Math.PI) / 180)
  const lngOffset = (distanceKm / (111 * Math.cos((centerLat * Math.PI) / 180))) * Math.sin((bearingDeg * Math.PI) / 180)
  return [centerLat + latOffset, centerLng + lngOffset]
}

function roundCoord(value: number): string {
  return value.toFixed(5)
}

/** Stable key from coordinate pairs so availability-only refreshes do not re-fit the map. */
export function buildMapBoundsKey(points: [number, number][]): string {
  return points
    .map(([lat, lng]) => `${roundCoord(lat)},${roundCoord(lng)}`)
    .sort()
    .join('|')
}

function isMapAlive(map: ReturnType<typeof useMap>): boolean {
  try {
    const container = map.getContainer()
    return Boolean(container?.parentNode && map.getPane('mapPane'))
  } catch {
    return false
  }
}

function FitBounds({ points, boundsKey }: { points: [number, number][]; boundsKey: string }) {
  const map = useMap()
  const lastKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (points.length === 0) return
    if (lastKeyRef.current === boundsKey) return
    lastKeyRef.current = boundsKey

    // Avoid animate: zoomtransitionend after unmount/remount throws on missing _leaflet_pos.
    try {
      map.stop()
      if (!isMapAlive(map)) return
      if (points.length === 1) {
        map.setView(points[0], 12, { animate: false })
      } else {
        map.fitBounds(points, { padding: [22, 22], animate: false })
      }
    } catch {
      // Map pane may already be torn down during React remount.
    }

    return () => {
      try {
        map.stop()
      } catch {
        // ignore
      }
    }
  }, [map, points, boundsKey])

  return null
}

export const FEDispatchMap = memo(function FEDispatchMap({
  recommendations,
  primaryUserId,
  backupUserId,
  additionalUserId,
  referenceType,
  referenceLabel,
  storeCode,
  storeLatitude,
  storeLongitude,
  conflictUserId,
  onPinClick,
}: FEDispatchMapProps) {
  const resolvedAdditionalUserId = additionalUserId ?? backupUserId
  const center: [number, number] = useMemo(() => {
    if (
      storeLatitude != null &&
      Number.isFinite(storeLatitude) &&
      storeLongitude != null &&
      Number.isFinite(storeLongitude)
    ) {
      return [Number(storeLatitude), Number(storeLongitude)]
    }
    return DEFAULT_CENTER
  }, [storeLatitude, storeLongitude])

  const pins = useMemo(() => {
    const list = recommendations.slice(0, 6)
    return list.map((fe, i) => {
      const rawLat = Number(fe.latitude)
      const rawLng = Number(fe.longitude)
      const hasCoords = Number.isFinite(rawLat) && Number.isFinite(rawLng)
      const distanceKm = Math.max(1, Number(fe.distance_km ?? 8))
      const bearing = 35 + i * 48
      const [lat, lng] = hasCoords
        ? [rawLat, rawLng]
        : offsetLatLng(center[0], center[1], distanceKm, bearing + (Y_OFFSETS[i] ?? 0))
      return { fe, lat, lng }
    })
  }, [recommendations, center])

  const storeLabel = referenceLabel
    ? referenceType === 'location'
      ? `Location: ${referenceLabel}`
      : storeCode
        ? `Store ${storeCode}: ${referenceLabel}`
        : referenceLabel
    : storeCode
      ? `Store ${storeCode}`
      : referenceType === 'location'
        ? 'Location'
        : 'Store'
  const points: [number, number][] = useMemo(
    () => [center, ...pins.map((p) => [p.lat, p.lng] as [number, number])],
    [center, pins],
  )
  const boundsKey = useMemo(() => buildMapBoundsKey(points), [points])

  return (
    <Box
      border="0.5px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
      mb={2}
      position="relative"
      zIndex={0}
      sx={{ isolation: 'isolate' }}
    >
      <HStack
        justify="space-between"
        px={3}
        py={1.5}
        bg="gray.50"
        borderBottom="0.5px solid"
        borderColor="gray.200"
      >
        <HStack spacing={1.5}>
          <Map size={13} color="#205BF3" />
          <Text fontSize="11px" fontWeight="500" color="gray.600">
            Location overview
          </Text>
        </HStack>
        <Text fontSize="10px" color="gray.500">
          Tap pin to assign
        </Text>
      </HStack>
      <Box h="320px">
        <MapContainer center={center} zoom={11} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
          <FitBounds points={points} boundsKey={boundsKey} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <CircleMarker center={center} radius={10} pathOptions={{ color: '#185fa5', fillColor: '#185fa5', fillOpacity: 0.9 }}>
            <Tooltip direction="top" offset={[0, -6]} opacity={1} permanent>
              {storeLabel}
            </Tooltip>
          </CircleMarker>

          {pins.map(({ fe, lat, lng }) => {
            const uid = fe.user_id ?? null
            const isPrimary = uid != null && uid === primaryUserId
            const isBackup = uid != null && uid === resolvedAdditionalUserId
            const isConflict = uid != null && uid === conflictUserId
            const col = pinColor(fe, isPrimary, isBackup, isConflict)
            const op = feOpacity(fe, isPrimary, isBackup)
            const status = isPrimary ? 'P' : isBackup ? 'B' : isConflict ? '!' : fe.is_locked ? '🔒' : getInitials(fe.full_name || '?')
            return (
              <CircleMarker
                key={`${fe.field_engineer_id}-${uid ?? 'n'}`}
                center={[lat, lng]}
                radius={9}
                pathOptions={{
                  color: isPrimary || isBackup ? '#ffffff' : col,
                  weight: isPrimary || isBackup ? 3 : 2,
                  fillColor: col,
                  fillOpacity: op,
                }}
                eventHandlers={onPinClick ? { click: () => onPinClick(fe) } : undefined}
              >
                <Tooltip direction="top" offset={[0, -6]} opacity={1}>
                  {fe.full_name} · {status} · {fe.distance_km != null ? `${Math.round(fe.distance_km)}km` : '—'}
                </Tooltip>
              </CircleMarker>
            )
          })}
        </MapContainer>
      </Box>
    </Box>
  )
})
